import { FolderOpenIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CollectionNode } from '@/services/types/collection-types';

import { formatDateString } from '../../utils/fileUtils';
import { Collapsible, InfoRow } from './InspectorComponents';

interface FolderInspectorProps {
  folder: CollectionNode;
}

export const FolderInspector = ({ folder }: FolderInspectorProps) => {
  return (
    <div className="h-full flex flex-col p-4">
      <div className="flex items-center gap-2 mb-4">
        <FolderOpenIcon className="h-5 w-5 text-primary flex-shrink-0" />
        <div className="flex-1 truncate">
          <h2 className="text-base font-medium truncate">{folder.name}</h2>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-medium mb-2">Folder Information</h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Files</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline">{folder.blob_count}</Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{folder.blob_count} files in this folder</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Subfolders</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline">{folder.child_count}</Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{folder.child_count} subfolders</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </div>

        <Separator className="my-2" />

        <div>
          <h3 className="text-sm font-medium mb-2">Timestamps</h3>
          <div className="space-y-2">
            <InfoRow
              label="Created"
              value={formatDateString(folder.created_at, 'relative')}
            />
            <InfoRow
              label="Modified"
              value={formatDateString(folder.modified_at, 'relative')}
            />
          </div>
        </div>

        <Collapsible title="Technical Details">
          <div className="space-y-2">
            <InfoRow label="Folder ID" value={folder.id} />
            <InfoRow label="Matter ID" value={folder.matter_id} />
          </div>
        </Collapsible>
      </div>
    </div>
  );
};

export default FolderInspector;
